import React, { useState, useEffect, useCallback } from 'react';
import {
    Button, Typography, Tag, Space, Spin, notification, Collapse, Popover,
    Tooltip, Statistic, Row, Col, Drawer, Modal, Tabs, Table, Input,
} from 'antd';
import {
    ArrowLeftOutlined, EditOutlined, ExperimentOutlined, PlusOutlined,
    AppstoreOutlined, FunctionOutlined, TeamOutlined,
    EyeOutlined, DeleteOutlined, DownOutlined, InfoCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import {
    getAccountingPlans, getConcepts, getPlanVariables,
    getWorkspacesByPlan, deleteConcept,
} from '../../../api/payments';
import { EConceptType } from '../../FinanceEngine/enums/types.enum';
import { BOOKING_FIELDS, BOOKING_VARIABLES } from '../../FinanceEngine/constants/booking.constants';
import { OPTIONS as OPERATORS } from '../../FinanceEngine/constants/common.constants';
import { FREQUENCY_COLORS } from '../common/constants';
import { PlanEditorModal } from './PlanEditorModal';
import { PlanEvalDrawer } from './PlanEvalDrawer';
import { ConceptEditorModal } from '../Concepts/ConceptEditorModal';
import styles from '../common/styles.module.scss';

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

/* ─── Interfaces ─── */

interface ConceptItem {
  _id: string;
  name: string;
  frequency?: string;
  conditions: any[];
  operation: string;
}

interface VariableItem {
  _id: string;
  name: string;
  keyName?: string;
  value: any;
  planID?: string;
}

interface WorkspaceItem {
  _id: string;
  name: string;
}

/* ─── Helpers: condiciones ─── */

const getFieldLabel = (field: string): string => {
    const found = BOOKING_FIELDS.find((f: any) => f.value === field);
    return found ? found.label : field;
};

const getOperatorLabel = (op: string): string => {
    const found = OPERATORS.find((o: any) => o.value === op);
    return found ? found.label : op;
};

const getOperatorColor = (op: string): string => {
    if (op === 'CONTAINS' || op === 'EQUALS') return '#87d068';
    if (op === 'NOT_CONTAINS' || op === 'NOT_EQUALS') return '#ff7a45';
    if (op.includes('GREATER')) return '#177DDC';
    if (op.includes('LESS')) return '#FAAD14';
    return '#8c8c8c';
};

/* ─── Helpers: fórmula visual ─── */

interface FormulaToken {
  type: 'variable' | 'booking' | 'text' | 'operator' | 'function';
  value: string;
  label?: string;
  color?: string;
  notFound?: boolean;
}

const tokenizeText = (text: string): FormulaToken[] => {
    const tokens: FormulaToken[] = [];
    // Split by operators and functions
    const parts = text.split(/(\s*[+\-*/]\s*|\s*\?\s*|\s*:\s*)/);

    parts.forEach((part) => {
        const trimmed = part.trim();
        if (!trimmed) return;

        if (['+', '-', '*', '/'].includes(trimmed)) {
            tokens.push({ type: 'operator', value: trimmed });
        } else if (trimmed.match(/^(Math\.\w+|\.reduce|\.find|\.map|\.filter)/)) {
            tokens.push({ type: 'function', value: trimmed });
        } else {
            tokens.push({ type: 'text', value: part });
        }
    });

    return tokens;
};

const tokenizeFormula = (
    operation: string,
    allVariables: VariableItem[],
): FormulaToken[] => {
    if (!operation) return [];
    const tokens: FormulaToken[] = [];
    const regex = /(\{\{variable\.(\w+)\}\}|\{\{booking\.([^}]+)\}\})/g;
    let lastIndex = 0;
    let match = regex.exec(operation);

    while (match !== null) {
        if (match.index > lastIndex) {
            const textBefore = operation.substring(lastIndex, match.index);
            tokens.push(...tokenizeText(textBefore));
        }

        if (match[2]) {
            const varId = match[2];
            const found = allVariables.find((v) => v._id === varId);
            tokens.push({
                type: 'variable',
                value: match[0],
                label: found ? found.name : varId,
                color: found ? (found.planID ? '#C9D6E8' : '#87d068') : '#ff4d4f',
                notFound: !found,
            });
        } else if (match[3]) {
            const fieldId = match[3];
            const found = BOOKING_VARIABLES.find((v: any) => v._id === fieldId);
            const fieldMatch = BOOKING_FIELDS.find((f: any) => f.value === fieldId);
            tokens.push({
                type: 'booking',
                value: match[0],
                label: found ? found.name : (fieldMatch ? fieldMatch.label : fieldId),
                color: '#2db7f5',
            });
        }

        lastIndex = match.index + match[0].length;
        match = regex.exec(operation);
    }

    if (lastIndex < operation.length) {
        tokens.push(...tokenizeText(operation.substring(lastIndex)));
    }

    return tokens;
};

/* ─── Helpers: explicación en lenguaje natural ─── */

const getBookingFieldLabel = (fieldId: string): string => {
    const found = BOOKING_VARIABLES.find((v: any) => v._id === fieldId);
    if (found) return found.name;
    const fieldMatch = BOOKING_FIELDS.find((f: any) => f.value === fieldId);
    return fieldMatch ? fieldMatch.label : fieldId;
};

const getOperatorWord = (op: string): string => {
    switch (op.trim()) {
    case '+': return 'se le suma';
    case '-': return 'se le resta';
    case '*': return 'se multiplica por';
    case '/': return 'se divide entre';
    default: return op;
    }
};

interface FormulaSegment {
  variableName: string;
  lookupFields: string[];
  isBooking: boolean;
}

const parseFormulaSegments = (
    operation: string,
    allVariables: VariableItem[],
): { segments: FormulaSegment[]; operators: string[] } => {
    const segments: FormulaSegment[] = [];
    const operators: string[] = [];

    // Split by top-level operators (+ - outside of brackets)
    // Simplified: split by operators that have spaces around them
    const parts = operation.split(/\s*([+-])\s*(?={{)/);

    const processChunk = (chunk: string) => {
    // Match variable with optional lookups: {{variable.ID}}["{{booking.field}}"]["{{booking.field2}}"]
        const varMatch = chunk.match(/\{\{variable\.(\w+)\}\}((?:\["[^"]*"\])*)/);
        if (varMatch) {
            const varId = varMatch[1];
            const found = allVariables.find((v) => v._id === varId);
            const lookupPart = varMatch[2] || '';
            const lookups: string[] = [];

            const lookupRegex = /\["{{booking\.([^}]+)}}"\]/g;
            let lm = lookupRegex.exec(lookupPart);
            while (lm !== null) {
                lookups.push(getBookingFieldLabel(lm[1]));
                lm = lookupRegex.exec(lookupPart);
            }

            // Also check for static lookups like ["key"]
            if (lookups.length === 0) {
                const staticLookup = /\["([^{][^"]*)"\]/g;
                let sm = staticLookup.exec(lookupPart);
                while (sm !== null) {
                    lookups.push(sm[1]);
                    sm = staticLookup.exec(lookupPart);
                }
            }

            segments.push({
                variableName: found ? found.name : varId,
                lookupFields: lookups,
                isBooking: false,
            });
            return;
        }

        // Match booking field: {{booking.field}}
        const bookingMatch = chunk.match(/\{\{booking\.([^}]+)\}\}/);
        if (bookingMatch) {
            segments.push({
                variableName: getBookingFieldLabel(bookingMatch[1]),
                lookupFields: [],
                isBooking: true,
            });
            return;
        }

        // Plain number or expression
        const trimmed = chunk.trim();
        if (trimmed && trimmed !== '+' && trimmed !== '-' && trimmed !== '*' && trimmed !== '/') {
            segments.push({
                variableName: trimmed,
                lookupFields: [],
                isBooking: false,
            });
        }
    };

    // More robust splitting: find each {{...}} block with its lookups, and operators between them
    const topRegex = /(\{\{(?:variable|booking)\.[^}]+\}\}(?:\["[^"]*"\])*|[0-9.]+)/g;
    const opRegex = /\s*([+\-*/])\s*/g;
    let tm = topRegex.exec(operation);
    let lastEnd = 0;

    while (tm !== null) {
    // Check for operator between last match and this one
        const between = operation.substring(lastEnd, tm.index).trim();
        if (between && ['+', '-', '*', '/'].includes(between)) {
            operators.push(between);
        }
        processChunk(tm[0]);
        lastEnd = tm.index + tm[0].length;
        tm = topRegex.exec(operation);
    }

    return { segments, operators };
};

const generateExplanation = (
    operation: string,
    allVariables: VariableItem[],
): React.ReactNode => {
    if (!operation) return null;

    const { segments, operators } = parseFormulaSegments(operation, allVariables);
    if (segments.length === 0) return null;

    const steps: React.ReactNode[] = [];

    segments.forEach((seg, i) => {
        let description: string;

        if (seg.isBooking) {
            description = `Usa el valor de "${seg.variableName}" del pedido`;
        } else if (seg.lookupFields.length > 0) {
            const fields = seg.lookupFields.join(' y ');
            description = `Busca en la variable "${seg.variableName}" el valor que corresponde al ${fields} del pedido`;
        } else if (!Number.isNaN(Number(seg.variableName))) {
            description = `Usa el valor fijo ${seg.variableName}`;
        } else {
            description = `Usa el valor de "${seg.variableName}"`;
        }

        if (i > 0 && operators[i - 1]) {
            const opWord = getOperatorWord(operators[i - 1]);
            description = `${opWord.charAt(0).toUpperCase()}${opWord.slice(1)}: ${description.charAt(0).toLowerCase()}${description.slice(1)}`;
        }

        steps.push(
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} style={{ marginBottom: 6, display: 'flex', gap: 8 }}>
                <span style={{
                    color: '#177DDC', fontWeight: 600, minWidth: 20,
                }}
                >
                    {`${i + 1}.`}
                </span>
                <span style={{ color: '#d9d9d9' }}>{description}</span>
            </div>,
        );
    });

    return (
        <div style={{ maxWidth: 400 }}>
            <div style={{ marginBottom: 12 }}>
                <Text strong style={{ color: '#fff', fontSize: 13 }}>
                    ¿Cómo se calcula?
                </Text>
            </div>
            {steps}
            <div style={{
                marginTop: 12,
                paddingTop: 8,
                borderTop: '1px solid #303030',
            }}
            >
                <Text type="secondary" style={{ fontSize: 11 }}>
                    Los valores en verde son variables configurables.
                    Los valores en azul se obtienen automáticamente de cada pedido.
                </Text>
            </div>
        </div>
    );
};

/* ─── Helpers: tipo de variable ─── */

type VarType = 'number' | 'lookup' | 'reference' | 'conditional' | 'expression' | 'text';

const detectVarType = (value: any): VarType => {
    const strVal = String(value || '');
    if (!strVal) return 'text';
    if (!Number.isNaN(Number(strVal)) && strVal.length < 20) return 'number';
    if (strVal.startsWith('{') && strVal.includes(':')) {
        try { JSON.parse(strVal); return 'lookup'; } catch { /* fall */ }
    }
    if (/^\{\{booking\.[^}]+\}\}$/.test(strVal.trim())) return 'reference';
    if (strVal.includes('{{booking.') || strVal.includes('===') || strVal.includes('!==')) return 'conditional';
    if (strVal.startsWith('[') || strVal.includes('.reduce') || strVal.includes('.find') || strVal.includes('.filter')) return 'expression';
    return 'text';
};

const VAR_TYPE_CONFIG: Record<VarType, { label: string; color: string }> = {
    number: { label: 'Valor fijo', color: 'green' },
    lookup: { label: 'Tabla de búsqueda', color: 'gold' },
    reference: { label: 'Referencia', color: 'cyan' },
    conditional: { label: 'Condicional', color: 'blue' },
    expression: { label: 'Expresión', color: 'purple' },
    text: { label: 'Texto', color: 'default' },
};

/* ─── Component ─── */

const PlanDetailView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [plan, setPlan] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [concepts, setConcepts] = useState<ConceptItem[]>([]);
    const [variables, setVariables] = useState<VariableItem[]>([]);
    const [conceptsLoading, setConceptsLoading] = useState(false);
    const [variablesLoading, setVariablesLoading] = useState(false);
    const [expandedConcept, setExpandedConcept] = useState<string | string[]>([]);

    // Workspaces: lazy
    const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
    const [workspacesLoading, setWorkspacesLoading] = useState(false);
    const [workspacesLoaded, setWorkspacesLoaded] = useState(false);
    const [workspacesDrawerVisible, setWorkspacesDrawerVisible] = useState(false);

    // Variable detail modal
    const [varDetailVisible, setVarDetailVisible] = useState(false);
    const [selectedVar, setSelectedVar] = useState<VariableItem | null>(null);

    // Editor
    const [editorVisible, setEditorVisible] = useState(false);

    // Eval drawer
    const [evalDrawerVisible, setEvalDrawerVisible] = useState(false);
    const [evalConceptTarget, setEvalConceptTarget] = useState<ConceptItem | null>(null);

    // Concept editor
    const [conceptEditorVisible, setConceptEditorVisible] = useState(false);
    const [editingConcept, setEditingConcept] = useState<any>(null);

    // Variable search (local filter)
    const [varSearch, setVarSearch] = useState('');
    const [varTab, setVarTab] = useState('global');

    const fetchPlan = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const { data } = await getAccountingPlans({
                paginator: { page: 1, limit: 100 },
            });
            const allPlans = data.data?.data || data.data || [];
            const found = allPlans.find((p: any) => p._id === id);
            if (found) {
                setPlan(found);
            } else {
                notification.error({ message: 'Plan no encontrado' });
                navigate('/v2/finance-engine');
            }
        } catch {
            notification.error({ message: 'Error cargando plan' });
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    const fetchConcepts = useCallback(async () => {
        if (!id) return;
        setConceptsLoading(true);
        try {
            const res = await getConcepts(id, EConceptType.BOOKING);
            setConcepts(res.data?.data?.data || res.data?.data || res.data || []);
        } catch {
            notification.error({ message: 'Error cargando conceptos' });
        } finally {
            setConceptsLoading(false);
        }
    }, [id]);

    const fetchVariables = useCallback(async () => {
        if (!id) return;
        setVariablesLoading(true);
        try {
            const res = await getPlanVariables(id);
            setVariables(res.data?.data?.data || res.data?.data || res.data || []);
        } catch {
            notification.error({ message: 'Error cargando variables' });
        } finally {
            setVariablesLoading(false);
        }
    }, [id]);

    const fetchWorkspaces = useCallback(async () => {
        if (!id) return;
        setWorkspacesLoading(true);
        try {
            const res = await getWorkspacesByPlan(id);
            setWorkspaces(res.data?.data?.data || res.data?.data || res.data || []);
            setWorkspacesLoaded(true);
        } catch {
            notification.error({ message: 'Error cargando workspaces' });
        } finally {
            setWorkspacesLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchPlan(); }, [fetchPlan]);

    useEffect(() => {
        if (plan) {
            fetchConcepts();
            fetchVariables();
        }
    }, [plan, fetchConcepts, fetchVariables]);

    const handleShowWorkspaces = () => {
        if (!workspacesLoaded) fetchWorkspaces();
        setWorkspacesDrawerVisible(true);
    };

    const handleDeleteConcept = (concept: ConceptItem) => {
        Modal.confirm({
            title: 'Eliminar concepto',
            content: `¿Eliminar "${concept.name}"? Esta acción no se puede deshacer.`,
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteConcept(id as string, concept._id);
                    notification.success({ message: 'Concepto eliminado' });
                    fetchConcepts();
                } catch {
                    notification.error({ message: 'Error eliminando concepto' });
                }
            },
        });
    };

    const handleEditorClose = (updated?: any) => {
        setEditorVisible(false);
        if (updated) setPlan(updated);
    };

    const handleConceptEditorClose = (updatedConcept?: any) => {
        setConceptEditorVisible(false);
        setEditingConcept(null);
        if (updatedConcept) {
            fetchConcepts();
        }
    };

    const varSearchLower = varSearch.toLowerCase();
    const planVariables = variables.filter(
        (v) => v.planID && (!varSearch || v.name.toLowerCase().includes(varSearchLower)),
    );
    const globalVariables = variables.filter(
        (v) => !v.planID && (!varSearch || v.name.toLowerCase().includes(varSearchLower)),
    );

    /* ─── Render: formula visual ─── */

    const renderFormula = (operation: string) => {
        const tokens = tokenizeFormula(operation, variables);
        if (tokens.length === 0) {
            return <Text type="secondary">Sin fórmula definida</Text>;
        }

        return (
            <div style={{
                background: '#141414',
                border: '1px solid #303030',
                borderRadius: 8,
                padding: 16,
            }}
            >
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                }}
                >
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                        }}
                    >
                        Fórmula de cálculo
                    </Text>
                    <Popover
                        content={generateExplanation(operation, variables)}
                        trigger="click"
                        placement="rightTop"
                    >
                        <InfoCircleOutlined style={{ fontSize: 16, color: '#177DDC', cursor: 'pointer' }} />
                    </Popover>
                </div>
                <div style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
                }}
                >
                    {tokens.map((token, i) => {
                        const key = `${token.type}-${i}`;
                        switch (token.type) {
                        case 'variable':
                            return (
                                <Tooltip
                                    key={key}
                                    title={token.notFound ? 'Variable no encontrada' : `Variable: ${token.label}`}
                                >
                                    <Tag
                                        color={token.notFound ? 'error' : undefined}
                                        style={{
                                            margin: 0,
                                            color: token.color,
                                            background: 'rgba(135,208,104,0.1)',
                                            border: `1px solid ${token.color}40`,
                                            fontWeight: 600,
                                            fontSize: 14,
                                            padding: '3px 12px',
                                            textDecoration: token.notFound ? 'line-through' : undefined,
                                        }}
                                    >
                                        {token.label}
                                    </Tag>
                                </Tooltip>
                            );
                        case 'booking':
                            return (
                                <Tooltip
                                    key={key}
                                    title={`Campo del booking: ${token.label}`}
                                >
                                    <Tag
                                        style={{
                                            margin: 0,
                                            color: '#2db7f5',
                                            background: 'rgba(45,183,245,0.1)',
                                            border: '1px solid rgba(45,183,245,0.3)',
                                            fontWeight: 600,
                                            fontSize: 14,
                                            padding: '3px 12px',
                                        }}
                                    >
                                        {token.label}
                                    </Tag>
                                </Tooltip>
                            );
                        case 'operator':
                            return (
                                <span
                                    key={key}
                                    style={{
                                        fontSize: 20, fontWeight: 700, color: '#fff', padding: '0 4px',
                                    }}
                                >
                                    {token.value}
                                </span>
                            );
                        case 'function':
                            return (
                                <Tag
                                    key={key}
                                    color="purple"
                                    style={{ margin: 0, fontSize: 13 }}
                                >
                                    {token.value}
                                </Tag>
                            );
                        default:
                            return (
                                <span
                                    key={key}
                                    style={{ fontSize: 14, color: '#d9d9d9', fontFamily: "'Courier New', monospace" }}
                                >
                                    {token.value}
                                </span>
                            );
                        }
                    })}
                </div>
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #303030' }}>
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 11, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5,
                        }}
                    >
                        Fórmula original
                    </Text>
                    <Text
                        code
                        copyable
                        style={{ fontSize: 11, wordBreak: 'break-all' }}
                    >
                        {operation}
                    </Text>
                </div>
            </div>
        );
    };

    /* ─── Render: conditions ─── */

    const renderConditions = (conditions: any[]) => {
        if (!conditions || conditions.length === 0) {
            return (
                <Tag style={{ fontSize: 12 }}>
                    Aplica siempre
                </Tag>
            );
        }
        return (
            <Space size={4} wrap>
                {conditions.map((c: any, i: number) => (
                    <Tag
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        style={{
                            margin: 0,
                            fontSize: 12,
                            background: 'transparent',
                            border: '1px solid #434343',
                        }}
                    >
                        <span style={{ color: '#2db7f5' }}>
                            {getFieldLabel(c.field)}
                        </span>
                        {' '}
                        <span style={{
                            color: getOperatorColor(c.operator), fontWeight: 600,
                        }}
                        >
                            {getOperatorLabel(c.operator)}
                        </span>
                        {' '}
                        <span style={{ color: '#FAAD14' }}>
                            {c.value}
                        </span>
                    </Tag>
                ))}
            </Space>
        );
    };

    const handleViewVariable = (v: VariableItem) => {
        setSelectedVar(v);
        setVarDetailVisible(true);
    };

    /* ─── Variable columns ─── */

    const variableSimpleColumns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => (
                <Text strong style={{ fontSize: 13 }}>{name}</Text>
            ),
        },
        {
            title: 'Tipo',
            dataIndex: 'value',
            key: 'type',
            width: 150,
            render: (value: any) => {
                const vType = detectVarType(value);
                const config = VAR_TYPE_CONFIG[vType];
                return <Tag color={config.color}>{config.label}</Tag>;
            },
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_: any, record: VariableItem) => (
                <Tooltip title="Ver valor">
                    <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewVariable(record)}
                    />
                </Tooltip>
            ),
        },
    ];

    /* ─── Render: variable detail modal ─── */

    const renderVarDetailContent = () => {
        if (!selectedVar) return null;
        const strVal = String(selectedVar.value || '');
        const vType = detectVarType(selectedVar.value);

        if (vType === 'number') {
            return (
                <div>
                    <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                        Esta variable tiene un valor numérico fijo que se usa directamente en la fórmula.
                    </Text>
                    <div style={{
                        background: '#141414',
                        border: '1px solid #303030',
                        borderRadius: 8,
                        padding: 20,
                        textAlign: 'center',
                    }}
                    >
                        <Text style={{
                            fontSize: 32, fontWeight: 700, color: '#87d068', fontFamily: "'Courier New', monospace",
                        }}
                        >
                            {strVal}
                        </Text>
                    </div>
                </div>
            );
        }

        if (vType === 'lookup') {
            try {
                const parsed = JSON.parse(strVal);
                const isNested = Object.values(parsed).some((v) => typeof v === 'object' && v !== null);
                const firstKey = Object.keys(parsed)[0] || '';
                const firstSubVal = isNested ? Object.keys(parsed[firstKey] || {})[0] || '' : '';
                const exampleVal = isNested ? parsed[firstKey]?.[firstSubVal] : parsed[firstKey];

                return (
                    <div>
                        <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                            Esta variable es una
                            {' '}
                            <strong>tabla de búsqueda</strong>
                            . Cuando se usa en una fórmula, el sistema busca el valor correspondiente
                            según las claves configuradas en la tabla.
                        </Text>
                        {isNested ? (
                            <Row gutter={[12, 12]}>
                                {Object.entries(parsed).map(([country, methods]: [string, any]) => (
                                    <Col key={country} span={12}>
                                        <div
                                            style={{
                                                background: '#141414',
                                                border: '1px solid #303030',
                                                borderRadius: 8,
                                                padding: 12,
                                                height: '100%',
                                            }}
                                        >
                                            <Text
                                                strong
                                                style={{
                                                    color: '#FAAD14', marginBottom: 8, display: 'block',
                                                }}
                                            >
                                                {country}
                                            </Text>
                                            {typeof methods === 'object' ? (
                                                <Table
                                                    size="small"
                                                    dataSource={Object.entries(methods).map(([k, v]) => ({
                                                        key: k, method: k, val: String(v),
                                                    }))}
                                                    columns={[
                                                        {
                                                            title: 'Clave',
                                                            dataIndex: 'method',
                                                            key: 'method',
                                                            render: (m: string) => <Tag>{m}</Tag>,
                                                        },
                                                        {
                                                            title: 'Valor',
                                                            dataIndex: 'val',
                                                            key: 'val',
                                                            render: (v: string) => (
                                                                <Text style={{
                                                                    color: '#87d068',
                                                                    fontFamily: "'Courier New', monospace",
                                                                }}
                                                                >
                                                                    {v}
                                                                </Text>
                                                            ),
                                                        },
                                                    ]}
                                                    pagination={false}
                                                />
                                            ) : (
                                                <Text style={{
                                                    color: '#87d068',
                                                    fontFamily: "'Courier New', monospace",
                                                }}
                                                >
                                                    {String(methods)}
                                                </Text>
                                            )}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                        // Single-level lookup: country → value
                            <Table
                                size="small"
                                dataSource={Object.entries(parsed).map(([k, v]) => ({
                                    key: k, country: k, val: String(v),
                                }))}
                                columns={[
                                    {
                                        title: 'País / Clave',
                                        dataIndex: 'country',
                                        key: 'country',
                                        render: (c: string) => <Tag color="gold">{c}</Tag>,
                                    },
                                    {
                                        title: 'Valor',
                                        dataIndex: 'val',
                                        key: 'val',
                                        render: (v: string) => (
                                            <Text style={{ color: '#87d068', fontFamily: "'Courier New', monospace" }}>
                                                {v}
                                            </Text>
                                        ),
                                    },
                                ]}
                                pagination={false}
                                style={{
                                    background: '#141414',
                                    borderRadius: 8,
                                    border: '1px solid #303030',
                                }}
                            />
                        )}
                        <div style={{
                            marginTop: 12,
                            padding: 12,
                            background: 'rgba(23,125,220,0.08)',
                            borderRadius: 8,
                            border: '1px solid rgba(23,125,220,0.2)',
                        }}
                        >
                            <InfoCircleOutlined style={{ color: '#177DDC', marginRight: 8 }} />
                            <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                                {isNested
                                    ? `Ejemplo: para las claves "${firstKey}" y "${firstSubVal}", el valor sería ${exampleVal}.`
                                    : `Ejemplo: para la clave "${firstKey}", el valor sería ${exampleVal}.`}
                            </Text>
                        </div>
                    </div>
                );
            } catch {
                return <Text code copyable>{strVal}</Text>;
            }
        }

        if (vType === 'reference') {
            const fieldMatch = strVal.match(/\{\{booking\.(.+)\}\}/);
            const fieldName = fieldMatch ? fieldMatch[1] : strVal;
            return (
                <div>
                    <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                        Esta variable toma directamente el valor del campo
                        {' '}
                        <strong>{fieldName}</strong>
                        {' '}
                        del pedido. No se evalúa ninguna condición, simplemente se reemplaza por el valor del campo.
                    </Text>
                    <div style={{
                        background: '#141414',
                        border: '1px solid #303030',
                        borderRadius: 8,
                        padding: 16,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                    }}
                    >
                        <Tag color="cyan" style={{ margin: 0, fontSize: 14, padding: '4px 12px' }}>
                            {fieldName}
                        </Tag>
                        <Text
                            code
                            copyable
                            style={{ fontSize: 13, color: '#8c8c8c' }}
                        >
                            {strVal}
                        </Text>
                    </div>
                    <div style={{
                        marginTop: 12,
                        padding: 12,
                        background: 'rgba(19, 194, 194, 0.08)',
                        borderRadius: 8,
                        border: '1px solid rgba(19, 194, 194, 0.2)',
                    }}
                    >
                        <InfoCircleOutlined style={{ color: '#13c2c2', marginRight: 8 }} />
                        <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                            Al calcular, esta variable se sustituye por el valor actual del campo en el pedido.
                        </Text>
                    </div>
                </div>
            );
        }

        if (vType === 'conditional') {
            return (
                <div>
                    <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                        Esta variable evalúa una
                        {' '}
                        <strong>condición</strong>
                        {' '}
                        basada en datos del pedido. Su valor cambia según se cumpla o no la condición.
                    </Text>
                    <div style={{
                        background: '#141414',
                        border: '1px solid #303030',
                        borderRadius: 8,
                        padding: 16,
                    }}
                    >
                        <Text
                            code
                            copyable
                            style={{ fontSize: 13, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
                        >
                            {strVal}
                        </Text>
                    </div>
                    <div style={{
                        marginTop: 12,
                        padding: 12,
                        background: 'rgba(23,125,220,0.08)',
                        borderRadius: 8,
                        border: '1px solid rgba(23,125,220,0.2)',
                    }}
                    >
                        <InfoCircleOutlined style={{ color: '#177DDC', marginRight: 8 }} />
                        <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                            El valor de esta variable depende de los datos del pedido al momento de calcular.
                        </Text>
                    </div>
                </div>
            );
        }

        if (vType === 'expression') {
            return (
                <div>
                    <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                        Esta variable contiene una
                        {' '}
                        <strong>expresión</strong>
                        {' '}
                        que se evalúa dinámicamente usando datos del pedido (como historial de estados, tiempos, etc.).
                    </Text>
                    <div style={{
                        background: '#141414',
                        border: '1px solid #303030',
                        borderRadius: 8,
                        padding: 16,
                    }}
                    >
                        <Text
                            code
                            copyable
                            style={{ fontSize: 13, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
                        >
                            {strVal}
                        </Text>
                    </div>
                </div>
            );
        }

        // text / default
        return (
            <div style={{
                background: '#141414',
                border: '1px solid #303030',
                borderRadius: 8,
                padding: 20,
            }}
            >
                <Text
                    copyable
                    style={{ fontSize: 14, color: '#87d068' }}
                >
                    {strVal}
                </Text>
            </div>
        );
    };

    /* ─── Loading / Not found ─── */

    if (loading) {
        return (
            <div className={styles.container} style={{ textAlign: 'center', paddingTop: 80 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!plan) return null;

    return (
        <div className={styles.container}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/v2/finance-engine?tab=plans')}
                    style={{ marginBottom: 16, padding: 0 }}
                >
                    Volver a Plans
                </Button>

                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                }}
                >
                    <div>
                        <Title level={2} style={{ margin: 0, color: '#fff' }}>{plan.name}</Title>
                        <Space style={{ marginTop: 8 }}>
                            <Tag
                                color={FREQUENCY_COLORS[plan.frequency] || 'default'}
                                style={{ fontSize: 13, padding: '2px 12px' }}
                            >
                                {plan.frequency}
                            </Tag>
                            <Text type="secondary">
                                {'Creado: '}
                                {moment(plan.createdAt).format('MMM DD, YYYY')}
                            </Text>
                            {plan.updatedAt && (
                                <Text type="secondary">
                                    {'Actualizado: '}
                                    {moment(plan.updatedAt).format('MMM DD, YYYY')}
                                </Text>
                            )}
                        </Space>
                    </div>
                    <Space>
                        <Tooltip title="Evaluar plan con un pedido real">
                            <Button
                                icon={<ExperimentOutlined />}
                                onClick={() => setEvalDrawerVisible(true)}
                            >
                                Probar
                            </Button>
                        </Tooltip>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => setEditorVisible(true)}
                        >
                            Editar
                        </Button>
                    </Space>
                </div>
            </div>

            {/* Stats */}
            <Row gutter={[24, 16]} style={{ marginBottom: 32 }}>
                <Col span={8}>
                    <div className={styles.statsCard} style={{ textAlign: 'center' }}>
                        <AppstoreOutlined style={{ fontSize: 20, color: '#177DDC', marginBottom: 4 }} />
                        <Statistic
                            title="Conceptos"
                            value={conceptsLoading ? '-' : concepts.length}
                            valueStyle={{ fontSize: 24, color: '#fff' }}
                        />
                    </div>
                </Col>
                <Col span={8}>
                    <div className={styles.statsCard} style={{ textAlign: 'center' }}>
                        <FunctionOutlined style={{ fontSize: 20, color: '#87d068', marginBottom: 4 }} />
                        <Statistic
                            title="Variables"
                            value={variablesLoading ? '-' : variables.length}
                            valueStyle={{ fontSize: 24, color: '#fff' }}
                        />
                    </div>
                </Col>
                <Col span={8}>
                    <Tooltip title="Ver workspaces asignados">
                        <div
                            className={styles.statsCard}
                            style={{ textAlign: 'center', cursor: 'pointer' }}
                            onClick={handleShowWorkspaces}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleShowWorkspaces()}
                        >
                            <div style={{
                                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8,
                            }}
                            >
                                <TeamOutlined style={{ fontSize: 20, color: '#FAAD14' }} />
                                <EyeOutlined style={{ fontSize: 14, color: '#8c8c8c' }} />
                            </div>
                            <Statistic
                                title="Workspaces"
                                value={workspacesLoaded ? workspaces.length : '—'}
                                valueStyle={{ fontSize: 24, color: '#fff' }}
                            />
                        </div>
                    </Tooltip>
                </Col>
            </Row>

            {/* Content: Conceptos (left) + Variables (right) */}
            <Row gutter={24}>
                <Col span={14}>
                    <div className={styles.tableWrapper} style={{ marginBottom: 24 }}>
                        <div style={{
                            padding: '16px 20px',
                            borderBottom: '1px solid #303030',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                        >
                            <Text strong style={{ fontSize: 14 }}>
                                {'Conceptos '}
                                {!conceptsLoading && <Tag>{concepts.length}</Tag>}
                            </Text>
                            <Button
                                type="primary"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setEditingConcept({ planID: id });
                                    setConceptEditorVisible(true);
                                }}
                            >
                                Agregar
                            </Button>
                        </div>

                        {conceptsLoading && (
                            <div style={{ padding: 24, textAlign: 'center' }}>
                                <Spin size="small" />
                            </div>
                        )}

                        {!conceptsLoading && concepts.length === 0 && (
                            <div style={{ padding: '24px 20px' }}>
                                <Text type="secondary">
                                    Este plan no tiene conceptos configurados
                                </Text>
                            </div>
                        )}

                        {!conceptsLoading && concepts.length > 0 && (
                            <Collapse
                                accordion
                                activeKey={expandedConcept}
                                onChange={(key) => setExpandedConcept(key)}
                                expandIcon={({ isActive }) => (
                                    <DownOutlined
                                        rotate={isActive ? 180 : 0}
                                        style={{ fontSize: 11, color: '#8c8c8c' }}
                                    />
                                )}
                                style={{ background: 'transparent', border: 'none', padding: '0 8px' }}
                            >
                                {concepts.map((concept) => (
                                    <Panel
                                        key={concept._id}
                                        style={{
                                            borderBottom: '1px solid #303030',
                                            borderRadius: 0,
                                            background: 'transparent',
                                        }}
                                        header={(
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                            >
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0,
                                                }}
                                                >
                                                    <Text strong style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                                                        {concept.name}
                                                    </Text>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        {renderConditions(concept.conditions)}
                                                    </div>
                                                </div>
                                                <Space
                                                    size={6}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <Tooltip title="Evaluar concepto">
                                                        <Button
                                                            type="text"
                                                            icon={<ExperimentOutlined style={{ fontSize: 16 }} />}
                                                            onClick={() => {
                                                                setEvalConceptTarget(concept);
                                                                setEvalDrawerVisible(true);
                                                            }}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title="Editar concepto">
                                                        <Button
                                                            type="text"
                                                            icon={<EditOutlined style={{ fontSize: 16 }} />}
                                                            onClick={() => {
                                                                setEditingConcept(concept);
                                                                setConceptEditorVisible(true);
                                                            }}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title="Eliminar">
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined style={{ fontSize: 16 }} />}
                                                            onClick={() => handleDeleteConcept(concept)}
                                                        />
                                                    </Tooltip>
                                                </Space>
                                            </div>
                                        )}
                                    >
                                        {renderFormula(concept.operation)}
                                    </Panel>
                                ))}
                            </Collapse>
                        )}
                    </div>
                </Col>

                <Col span={10}>
                    <div className={styles.tableWrapper} style={{ marginBottom: 24, padding: 16 }}>
                        <Tabs
                            defaultActiveKey="global"
                            style={{ padding: 0 }}
                            tabBarStyle={{ marginBottom: 12 }}
                        >
                            <TabPane
                                tab={(
                                    <span>
                                        {'Globales '}
                                        {!variablesLoading && <Tag color="green">{globalVariables.length}</Tag>}
                                    </span>
                                )}
                                key="global"
                            >
                                <Input
                                    placeholder="Buscar variable..."
                                    prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                                    allowClear
                                    value={varSearch}
                                    onChange={(e) => setVarSearch(e.target.value)}
                                    style={{ marginBottom: 12 }}
                                />
                                {variablesLoading && (
                                    <div style={{ padding: 24, textAlign: 'center' }}>
                                        <Spin size="small" />
                                    </div>
                                )}
                                {!variablesLoading && globalVariables.length === 0 && (
                                    <div style={{ padding: '24px 16px' }}>
                                        <Text type="secondary">
                                            {varSearch ? 'No se encontraron variables globales' : 'No hay variables globales'}
                                        </Text>
                                    </div>
                                )}
                                {!variablesLoading && globalVariables.length > 0 && (
                                    <Table
                                        size="small"
                                        dataSource={globalVariables}
                                        columns={variableSimpleColumns}
                                        rowKey="_id"
                                        pagination={globalVariables.length > 10 ? { pageSize: 10, size: 'small' } : false}
                                    />
                                )}
                            </TabPane>
                            <TabPane
                                tab={(
                                    <span>
                                        {'Del Plan '}
                                        {!variablesLoading && <Tag>{planVariables.length}</Tag>}
                                    </span>
                                )}
                                key="plan"
                            >
                                <Input
                                    placeholder="Buscar variable..."
                                    prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                                    allowClear
                                    value={varSearch}
                                    onChange={(e) => setVarSearch(e.target.value)}
                                    style={{ marginBottom: 12 }}
                                />
                                {variablesLoading && (
                                    <div style={{ padding: 24, textAlign: 'center' }}>
                                        <Spin size="small" />
                                    </div>
                                )}
                                {!variablesLoading && planVariables.length === 0 && (
                                    <div style={{ padding: '24px 16px' }}>
                                        <Text type="secondary">
                                            {varSearch ? 'No se encontraron variables del plan' : 'Este plan usa solo variables globales'}
                                        </Text>
                                    </div>
                                )}
                                {!variablesLoading && planVariables.length > 0 && (
                                    <Table
                                        size="small"
                                        dataSource={planVariables}
                                        columns={variableSimpleColumns}
                                        rowKey="_id"
                                        pagination={planVariables.length > 10 ? { pageSize: 10, size: 'small' } : false}
                                    />
                                )}
                            </TabPane>
                        </Tabs>
                    </div>
                </Col>
            </Row>

            {/* Workspaces drawer */}
            <Drawer
                title={`Workspaces asignados (${workspacesLoaded ? workspaces.length : '...'})`}
                placement="right"
                width={420}
                visible={workspacesDrawerVisible}
                onClose={() => setWorkspacesDrawerVisible(false)}
            >
                {workspacesLoading && (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin />
                    </div>
                )}
                {workspacesLoaded && workspaces.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {workspaces.map((ws) => (
                            <Tag
                                key={ws._id}
                                style={{ margin: 0, padding: '4px 12px', fontSize: 13 }}
                            >
                                {ws.name || ws._id}
                            </Tag>
                        ))}
                    </div>
                )}
                {workspacesLoaded && workspaces.length === 0 && (
                    <Text type="secondary">
                        No hay workspaces asignados a este plan
                    </Text>
                )}
            </Drawer>

            <PlanEditorModal
                visible={editorVisible}
                plan={plan}
                onClose={handleEditorClose}
            />

            <Modal
                title={selectedVar?.name || 'Variable'}
                visible={varDetailVisible}
                onCancel={() => setVarDetailVisible(false)}
                footer={null}
                width={720}
                centered
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                {renderVarDetailContent()}
            </Modal>

            <PlanEvalDrawer
                visible={evalDrawerVisible}
                onClose={() => {
                    setEvalDrawerVisible(false);
                    setEvalConceptTarget(null);
                }}
                plan={plan}
                concepts={concepts}
                variables={variables}
                renderFormula={renderFormula}
                conceptTarget={evalConceptTarget}
            />

            <ConceptEditorModal
                visible={conceptEditorVisible}
                concept={editingConcept}
                planID={id as string}
                conceptType={EConceptType.BOOKING}
                onClose={handleConceptEditorClose}
            />
        </div>
    );
};

export { PlanDetailView };
